import * as StruktBase from '#src/StruktBase.ts';
import type * as T from '#types';

/**
 * Parameters for initializing a Strukt.
 * @template constructor - The constructor type.
 * @property {constructor} constructor - The constructor function.
 * @property {(keyof ReturnType<constructor>)[]} [hidden] - Optional keys to be hidden.
 */
export type params<constructor extends T.anyConstructor> = {
	constructor: constructor;
	hidden?: (keyof ReturnType<constructor>)[];
};

export type basicStruktClass<constructor extends T.anyConstructor> = {
	new (
		...params: Parameters<constructor>
	): StruktBase.BasicStrukt & ReturnType<constructor>;
};

export type struktClass<constructor extends T.anyConstructor> = {
	new (
		...params: Parameters<constructor>
	): StruktBase.ExtendedStrukt<
		Parameters<constructor>,
		ReturnType<constructor>
	> &
		ReturnType<constructor>;
};

// export const defaults = {};

/**
 * Initializes a Strukt class.
 * @template constructor - The constructor type.
 * @param {params<constructor>} params - The parameters for initialization.
 * @returns {struktClass<constructor>} The initialized Strukt class.
 * @example
 * class MyClass extends init({
 *   constructor (args: { x: number, y: number }) {
 *     return {
 *       x: args.x,
 *       y: args.y,
 *       sum: args.x + args.y
 *     };
 *   },
 *   hidden: ['sum']
 * }) {}
 * const instance = new MyClass({ x: 1, y: 2 });
 * console.log(instance); // Output: MyClass { x: 1, y: 2, sum: 3 }
 */
export const init = <constructor extends T.anyConstructor>(
	params: params<constructor>,
): struktClass<constructor> => {
	type args = Parameters<constructor>;
	type output = ReturnType<constructor>;

	const constructorFn: constructor = params.constructor;
	const hidden = params.hidden ?? [];

	class Strukt extends StruktBase.ExtendedStrukt<args, output> {
		constructor(...args: args) {
			const data = constructorFn(...args) as output;
			super({ data, hidden });
		}
	}

	return Strukt as struktClass<constructor>;
};

/**
 * Initializes a basic Strukt class with minimal functionality.
 * Unlike `init()` which creates a full-featured Strukt, this creates a lightweight version
 * that only includes the core data wrapping functionality without utility methods.
 * Use this when you only need the basic data wrapping and hidden properties functionality.
 * @template constructor - The constructor type.
 * @param {params<constructor>} params - The parameters for initialization.
 * @returns {basicStruktClass<constructor>} The initialized basic Strukt class with minimal features.
 * @example
 * class MyBasicClass extends initBasic({
 *   constructor (args: { x: number, y: number }) {
 *     return {
 *       x: args.x,
 *       y: args.y,
 *       sum: args.x + args.y
 *     };
 *   },
 *   hidden: ['sum']
 * }) {}
 * const instance = new MyBasicClass({ x: 1, y: 2 });
 * console.log(instance); // Output: MyBasicClass { x: 1, y: 2, sum: 3 }
 */
export const initBasic = <constructor extends T.anyConstructor>(
	params: params<constructor>,
): basicStruktClass<constructor> => {
	type args = Parameters<constructor>;
	type output = ReturnType<constructor>;

	const constructorFn: constructor = params.constructor;
	const hidden = params.hidden ?? [];

	class Strukt extends StruktBase.BasicStrukt {
		constructor(...args: args) {
			const data = constructorFn(...args) as output;
			super({ data, hidden });
		}
	}

	return Strukt as struktClass<constructor>;
};

/**
 * Type guard to check if a value is an ExtendedStrukt instance.
 * @param {unknown} value - The value to check.
 * @returns {boolean} True if the value is an ExtendedStrukt instance.
 * @example
 * if (isStrukt(someValue)) {
 *   // someValue is typed as ExtendedStrukt here
 *   console.log(someValue.$data());
 * }
 */
export const isStrukt = (
	value: unknown,
): value is StruktBase.ExtendedStrukt<any, any> =>
	value instanceof StruktBase.ExtendedStrukt;

/**
 * Type guard to check if a value is a BasicStrukt instance.
 * @param {unknown} value - The value to check.
 * @returns {boolean} True if the value is a BasicStrukt instance.
 * @example
 * if (isBasicStrukt(someValue)) {
 *   // someValue is typed as BasicStrukt here
 *   console.log(someValue);
 * }
 */
export const isBasicStrukt = (
	value: unknown,
): value is StruktBase.BasicStrukt => value instanceof StruktBase.BasicStrukt;
